import { ExternalLink } from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";

import { config } from "~/config";
import type { FirebaseUserRaw } from "~/features/admin/users/types";

interface FirebaseUserSectionProps {
  firebaseUser: FirebaseUserRaw;
}

export function FirebaseUserSection({ firebaseUser }: FirebaseUserSectionProps) {
  const { t } = useTranslation();

  const rows: { label: string; value: React.ReactNode }[] = [
    {
      label: t("model.user.firebaseUser.uid"),
      value: (
        <span className="inline-flex items-center gap-1.5">
          <span className="font-mono text-12-regular">{firebaseUser.uid}</span>
          <a
            href={`https://console.firebase.google.com/u/0/project/${config.firebase.projectId}/authentication/users`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-5 w-5 items-center justify-center rounded-[var(--radius-xs)] text-fg-link hover:bg-bg-subtle"
            aria-label={t("model.user.firebaseUser.modelName")}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </span>
      ),
    },
    { label: t("model.user.firebaseUser.email"), value: firebaseUser.email || "—" },
    { label: t("model.user.firebaseUser.emailVerified"), value: firebaseUser.emailVerified.toString() },
    {
      label: t("model.user.firebaseUser.photoURL"),
      value: firebaseUser.photoURL ? (
        <img src={firebaseUser.photoURL} alt="profile pic" className="max-h-24 object-contain" />
      ) : (
        "—"
      ),
    },
    { label: t("model.user.firebaseUser.disabled"), value: firebaseUser.disabled ? "true" : "false" },
  ];

  return (
    <section className="space-y-2">
      <h2 className="text-16-bold leading-tight capitalize">{t("model.user.firebaseUser.modelName")}</h2>
      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default bg-white">
        <table className="w-full border-collapse">
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.label} className={i < rows.length - 1 ? "border-b border-border-default" : undefined}>
                <th className="w-44 bg-bg-subtle px-3.5 py-2 text-left text-12-medium text-fg-2 align-top">
                  {row.label}
                </th>
                <td className="px-3.5 py-2 text-14-regular text-fg-1">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
